import type { HttpsConfig } from './HttpsConfig.js';
import type { ShorthandRoute } from './Route.js';

export interface ServerConfig {
  /**
   * The domain name of the server. If multiple domains are specified, the first one will be
   * used as the primary domain used when opening the browser.
   * @default 'localhost'
   */
  domain?: string | string[];

  /**
   * Additional header key/value pairs to be added to every response.
   *
   * NOTE: The following header values from Cloudpack will "win" against by a user's config:
   * - `'Cache-Control': 'no-cache'`
   * - `'Access-Control-Allow-Origin': '*'`
   */
  requestHeaders?: Record<string, string>;

  /**
   * Port(s) to try when starting the server. If provided, an error will be thrown if none of
   * the ports are available.
   *
   * If no ports are provided, the server will attempt to find an available port.
   */
  port?: number | number[];

  /**
   * The default path under the domain to use when opening the browser.
   * @default ''
   */
  defaultPath?: string;

  /**
   * Whether to automatically open the browser when the server starts, or a specific URL to open.
   * (You can also use the `--open` or `--no-open` CLI options.)
   * @default true
   */
  open?: boolean | string;

  /**
   * The config settings for the https server.
   * Paths to files are accepted for 'ca', 'cert', 'key', and 'pfx' settings.
   * Everything else is passed directly to the https.createServer() method.
   * If not provided, the server will use http.
   */
  https?: true | HttpsConfig;

  /**
   * Whether to show the Cloudpack overlay (status badge and dialog) in the app UI.
   *
   * It's not recommended to change this unless the overlay is interfering with UI elements or tests
   * (and if it's only needed in a specific case, use the `--show-overlay <when>` CLI option).
   * @default 'always'
   */
  showOverlay?: 'always' | 'errors-only' | 'never';

  /**
   * @deprecated Moved to the root of the config.
   */
  routes?: ShorthandRoute[];
}
